/* ============================================================================
   RED LIGHT HERO - QUIZ CONTENT
   ============================================================================
   THIS IS THE ONLY FILE YOU NEED TO EDIT TO CHANGE THE QUIZZES.

   Online Store > Themes > ... > Edit code > assets > rlh-quiz-data.js

   Contents, in order:
     1. SETTINGS   - labels and links shared by every quiz
     2. PRODUCTS   - the devices a quiz can recommend, and their spec bullets
     3. QUIZZES    - the six quizzes: questions, rules and results

   THINGS TO KNOW BEFORE EDITING
   -----------------------------
   * Prices, product names, images and stock are NOT in this file. They come
     live from Shopify, so they are never out of date. You only set the
     product "handle" (the bit after /products/ in the URL).
   * Text is written between 'single quotes'. If your text contains an
     apostrophe, escape it with a backslash: 'it\'s' - or use "double quotes".
   * Items in a list are separated by commas. The last one does not need one.
   * Lines starting with // are notes to you and are ignored by the website.
   * After saving, hard-refresh the page (Ctrl/Cmd + Shift + R) to see changes.

   HOW A QUIZ DECIDES ITS ANSWER
   -----------------------------
   Each quiz has "rules", checked from TOP to BOTTOM. The FIRST rule that
   matches wins, and its "result" is shown. Anything below it is ignored.
   So put your most specific rules first and your catch-alls last.

     { when: { goal: 'skin', budget: ['b3','b4'] }, result: 'skin_mask' }
       ^ matches when goal is skin AND budget is b3 or b4.
         A question not mentioned in "when" is ignored, i.e. it matches any answer.

   "modifiers" run AFTER a result is chosen and can add extra lines to it.
   Use them for "if they also said X, mention Y" without duplicating a result.
   ========================================================================== */

window.RLH_QUIZ_DATA = {

  version: '2026-08-27',

  /* ==========================================================================
     FOOTNOTE 3 - copied word for word from the site-wide footer.
     Shown automatically at the bottom of any result that contains a line
     marked  h: true  (a statement that could be read as a health claim).
     If you change the footer wording, change it here too.
     ========================================================================== */
  footnote3: '³ Our products use clinical-grade LEDs similar to those in research but are not ARTG-listed medical devices and do not treat or prevent disease. Information is educational only. "Backed by Science" refers to studies on wavelengths and technologies used in our devices, not to our specific products themselves. Consult a healthcare professional before use.',

  /* ==========================================================================
     REFERENCES
     Every study a quiz line can cite. All of these are taken from your own
     research pages, and all links were checked on 27 August 2026.

     To cite one, add its key to a line:
       { t: 'Your sentence.', h: true, refs: ['skin_split_face_rct'] }

     Numbering on screen is automatic and per result, in the order they appear.

     Deliberately NOT included: ResearchGate pages, preprints, vendor PDFs,
     conference posters, and clinicaltrials.gov registrations for trials that
     have not reported. Those appear on your research pages but are not
     peer-reviewed primary sources, so they are weak things to cite in a
     sales flow.
     ========================================================================== */
  references: {

    // --- Skin, collagen and wrinkles -------------------------------------
    skin_split_face_rct: {
      label: 'Randomised, double-blind split-face trial, n=76, 633nm and 830nm LED twice a week for four weeks: reduced wrinkle depth and improved elasticity, with increased collagen and elastic fibres confirmed on biopsy',
      url: 'https://pubmed.ncbi.nlm.nih.gov/17566756/'
    },
    skin_collagen_density: {
      label: 'Wunsch and Matuschka 2014, 136 volunteers, 611-650nm LED twice a week for 15 weeks: improved complexion and increased collagen density. The authors found broadband light gave no advantage over red alone',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC3926176/'
    },
    skin_mask_630: {
      label: '15 women and 5 men aged 45-70, 630nm LED mask, 12 minutes twice a week for three months: reduced periocular wrinkle depth and increased dermal density. Single-arm study with no control group',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10311288/'
    },
    skin_mask_home: {
      label: '60 adults, home-use 630 and 850nm LED mask, 9 minutes five times a week for 12 weeks: 86.2% improved on blinded rater assessment against 16.7% on sham. Collagen itself was not measured',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11835066/'
    },
    skin_neck_device: {
      label: '70 participants, home 630 and 850nm LED neck device daily for 12 weeks: 77% improved on visual grading of neck wrinkles. Elasticity and collagen were not measured',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11743241/'
    },

    // --- Hair -------------------------------------------------------------
    hair_kim_2013: {
      label: 'Kim et al. 2013, 40 participants, 630/650/660nm LED helmet, randomised double-blind sham-controlled over 24 weeks: increased hair density and thickness',
      url: 'https://pubmed.ncbi.nlm.nih.gov/23551662/'
    },
    hair_lanzafame: {
      label: 'Lanzafame et al. 2013, randomised double-blind sham-controlled trial in men, helmet combining 21 lasers and 30 LEDs at 655nm on alternate days for 16 weeks: around 35% more hair than sham',
      url: 'https://pubmed.ncbi.nlm.nih.gov/24078483/'
    },
    hair_meta: {
      label: 'Lueangarun 2021 meta-analysis of home-use light devices for androgenetic alopecia: standardised mean difference of 1.27 (95% CI 0.96-1.59) for hair density against sham',
      url: 'https://jcadonline.com/laser-therapy-hair-loss/'
    },
    hair_kim_2023: {
      label: 'Kim 2023, 50 participants, helmet emitting 630-690nm plus near-infrared bands, 20 minutes daily for six months: 73% rated improved. Single-arm, no sham control',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10564188/'
    },

    // --- Sleep ------------------------------------------------------------
    sleep_athletes: {
      label: 'Zhao 2012, 20 female basketball players, 658nm LED for 30 minutes nightly for 14 days: improved sleep quality and higher melatonin. Compared against a no-treatment control rather than a sham',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC3499892/'
    },
    sleep_nurses: {
      label: 'Liao 2025, randomised controlled trial in 64 shift-working nurses, 660 and 850nm LED cap: insomnia severity score 4.3 against 12.6 in controls',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12187440/'
    },

    // --- Pain and inflammation -------------------------------------------
    pain_review: {
      label: '2022 review of photobiomodulation for acute and chronic musculoskeletal pain and fibromyalgia, covering both laser and LED sources',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC9980499/'
    },
    pain_tendinopathy: {
      label: 'Meta-analysis of 17 randomised trials: light therapy combined with exercise produced greater reductions in tendinopathy pain than sham plus exercise',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC8364035/'
    },
    pain_mechanism: {
      label: 'Hamblin 2017 review of how photobiomodulation reduces inflammation. A mechanism review rather than a clinical trial',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC5523874/'
    },
    pain_hand_oa: {
      label: 'Single-group trial in hand osteoarthritis, 850 and 940nm LED twice weekly for four weeks: reduced pain. Swelling and range of motion did not change significantly',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC10915305/'
    },

    // --- Muscle recovery and training ------------------------------------
    muscle_review: {
      label: 'Narrative review of 61 studies, reporting positive effects in 25 of 39 human studies of red and near-infrared light used around exercise',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC8706093/'
    },
    muscle_meta_pre: {
      label: 'Meta-analysis of 39 randomised trials (861 participants), 655-950nm before exercise: improved time to exhaustion, repetitions and blood lactate',
      url: 'https://link.springer.com/article/10.1007/s10103-017-2368-6'
    },
    muscle_meta_null: {
      label: '2024 meta-analysis of 16 trials: photobiomodulation aided fatigue recovery but did not improve strength or functional capacity in healthy people',
      url: 'https://link.springer.com/article/10.1007/s10103-023-03956-2'
    },

    // --- Dose --------------------------------------------------------------
    dose_low_irradiance: {
      label: 'Double-blind sham-controlled home-use trial, n=59, positive at 10 mW/cm² or below - successful human LED studies commonly sit far below the peak output a panel can produce',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11835066/'
    },
    dose_biphasic: {
      label: 'Huang and Hamblin 2009 on the biphasic dose response: a threshold, a wide effective plateau, then declining benefit at much higher doses',
      url: 'https://pubmed.ncbi.nlm.nih.gov/20011653/'
    },
    dose_russell: {
      label: 'Russell et al. 2005, facial trial in 31 people combining 633nm and 830nm LED over nine sessions in five weeks',
      url: 'https://pubmed.ncbi.nlm.nih.gov/16414908/'
    },

    // --- LED versus laser --------------------------------------------------
    led_vs_laser: {
      label: 'Review of 68 in-vitro and animal wound studies comparing LED with laser sources. Cited here only on the question of LED versus laser, not as clinical evidence',
      url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC4148276/'
    }
  },

  /* ==========================================================================
     1. SETTINGS - shared across all quizzes
     Anything here can be overridden per quiz, and some can be overridden in
     the theme editor when you place the section.
     ========================================================================== */
  settings: {
    // Buttons and labels
    primaryCtaTemplate: 'See {name}',      // {name} is replaced with the device name
    altCtaTemplate: 'View {name}',
    secondaryCtaLabel: 'Check session times',
    secondaryCtaUrl: 'https://redlighthero.com/pages/red-light-therapy-dosage-estimator',
    helpLabel: 'Prefer to ask a person?',
    helpUrl: 'https://wa.me/61433736985',
    resultHelpLabel: 'Not sure? Message us and we will tell you straight.',
    backLabel: 'Back',
    restartLabel: 'Start over',
    changeQuizLabel: 'Choose a different quiz',
    stepLabel: 'Step {n} of {total}',
    resultStepLabel: 'Your match',
    firstStepNote: 'Takes about 30 seconds. Nothing to fill in.',
    altsHeading: 'Also worth a look',
    soldOutLabel: 'Back soon',
    referencesHeading: 'The research behind this',
    referencesNote: 'These studies looked at the wavelengths and technologies our devices use. They were not carried out on Red Light Hero products.',

    // Pricing. The selling price always shows. The crossed-out price only
    // appears when Shopify has a compare-at price ABOVE the selling price -
    // never when it is the same, lower, or not set.
    fromLabel: 'From',            // shown when the device has variants at different prices
    wasLabel: 'Was',              // read out by screen readers before the crossed-out price
    showSaving: false,            // set true to add "Save 17%" next to the crossed-out price
    saveLabel: 'Save {percent}%',

    // Behaviour
    showSpecs: true,          // show the small spec bullets on the result card
    showAlternates: true,     // show the "also worth a look" cards
    maxAlternates: 2,         // how many alternates to show at most
    showPrice: true,
    scrollOnAnswer: true,     // scroll the quiz into view as they progress
    trackEvents: true,        // push events to window.dataLayer
    carryUtms: true,          // pass ad tracking params through to product links

    // Chooser screen (shown when more than one quiz is enabled)
    chooserEyebrow: 'Pick your starting point',
    chooserHeading: 'Which question are you trying to answer?',
    chooserSub: 'Every route ends in a straight recommendation. Pick whichever matches how you are thinking about it.',
    chooserCtaLabel: 'Start'
  },

  /* ==========================================================================
     2. PRODUCTS
     Every device a quiz can recommend. Add a new one by copying a block.

       handle       - the bit after /products/ in the Shopify URL. Required.
       variantMatch - OPTIONAL. Use when several devices share one product page
                      (the Elite panels do). Matches text in the variant name.
       name         - what to call it on the result card. Leave blank to use
                      the Shopify product title.
       tag          - the small label above the name
       specs        - the grey bullet list. Keep to about five lines.
       covers/space - used in some quiz copy. Optional.
     ========================================================================== */
  products: {

    hero60elite: {
      handle: 'heropanels-elite™-10-wavelength-red-nir-light-therapy-panels',
      variantMatch: 'Hero60 Elite',
      name: 'Hero60 Elite™',
      tag: 'Tabletop panel',
      specs: [
        '10 wavelengths (590-1072nm)',
        '60 x 3W medical-grade LEDs',
        '172 mW/cm² at 7.5cm',
        '31.6 x 21.4cm · 3kg',
        'Built-in stand · 3-year warranty'
      ],
      covers: 'Face, scalp, one joint or one muscle group at a time',
      space: 'Sits on a desk, bench or bedside table. No floor space needed.'
    },

    hero60: {
      handle: 'hero60-tabletop-red-light-therapy-panel',
      name: 'Hero60™',
      tag: 'Tabletop panel',
      specs: [
        '5 wavelengths (630/660/810/830/850nm)',
        '60 x 3W medical-grade LEDs',
        '202 mW/cm² at 7.5cm',
        '31.6 x 21.4cm · 3kg',
        '3-year warranty'
      ]
    },

    hero300elite: {
      handle: 'heropanels-elite™-10-wavelength-red-nir-light-therapy-panels',
      variantMatch: 'Hero300 Elite',
      name: 'Hero300 Elite™',
      tag: 'Half-body panel',
      specs: [
        '10 wavelengths (590-1072nm)',
        '300 x 3W medical-grade LEDs',
        '172 mW/cm² at 7.5cm · 60° lenses',
        '91 x 29.8cm · 11kg',
        '0 μT EMF at 7.5cm · 3-year warranty'
      ],
      covers: 'Torso, back or both legs - about half the body per session',
      space: 'Leans on a wall or sits in the HS1 floor stand. Needs about 1m of clear space in front.'
    },

    hero300: {
      handle: 'hero300-red-light-therapy-panel',
      name: 'Hero300™',
      tag: 'Half-body panel',
      specs: [
        '5 wavelengths (630/660/810/830/850nm)',
        '300 x 3W medical-grade LEDs',
        '209 mW/cm² at 7.5cm · 30° lenses',
        '91 x 29.8cm · 11kg',
        'HeroSync option · 3-year warranty'
      ]
    },

    hero960elite: {
      handle: 'heropanels-elite™-10-wavelength-red-nir-light-therapy-panels',
      variantMatch: 'Hero960 Elite',
      name: 'Hero960 Elite™',
      tag: 'Full-body panel',
      specs: [
        '10 wavelengths (590-1072nm)',
        '960 x 3W medical-grade LEDs',
        '172 mW/cm² at 7.5cm · 60° lenses',
        '180 x 47.3cm · 31kg',
        '3-year warranty'
      ],
      covers: 'Head to toe, front or back, in a single standing session',
      space: 'Needs a dedicated 2m x 1m area. Wall-mount or HS4 / HS5 stand.'
    },

    hero960: {
      handle: 'hero960-red-light-therapy-panel',
      name: 'Hero960™',
      tag: 'Full-body panel',
      specs: [
        '5 wavelengths (630/660/810/830/850nm)',
        '960 x 3W medical-grade LEDs',
        '217 mW/cm² at 7.5cm',
        '180 x 47.3cm · 31kg',
        'Red-only / NIR-only modes · 3-year warranty'
      ]
    },

    herosnug: {
      handle: 'herosnug-red-light-therapy-sleeping-bag-dual-mat-pod',
      name: 'HeroSnug™',
      tag: 'Full-body pod',
      specs: [
        '660 + 850nm (1:2 near-infrared)',
        '2,520 LEDs (7,560 chips)',
        '145 mW/cm² at contact',
        '180 x 80cm · unzips into two mats',
        '10-90 min timer · 1-year warranty'
      ],
      covers: 'Front and back at the same time - roughly 3m² of emitting surface',
      space: 'Rolls out on the floor (1.8 x 0.8m), rolls up for storage.'
    },

    herosnugelite: {
      handle: 'herosnug-elite™-red-light-therapy-sleeping-bag-dual-mat-pod',
      name: 'HeroSnug Elite™',
      tag: 'Full-body pod',
      specs: [
        '6 wavelengths including 1064nm',
        '2,520 LEDs (7,560 chips)',
        '145 mW/cm² at contact',
        '180 x 80cm · unzips into two mats',
        '10-90 min timer · 1-year warranty'
      ]
    },

    herospotlight: {
      handle: 'herospotlight™-portable-red-light-therapy-lamp-stand',
      name: 'HeroSpotlight™',
      tag: 'Portable lamp + stand',
      specs: [
        '660 + 850nm · 18 LEDs',
        '143 mW/cm² at 7.5cm',
        '2kg including stand',
        'Stand adjusts 97-200cm',
        '2-year warranty'
      ]
    },

    herotorch: {
      handle: 'herotorch-5-wavelength-red-light-therapy-torch',
      name: 'HeroTorch™',
      tag: 'Handheld torch',
      specs: [
        '5 wavelengths (460/630/660/850/900nm)',
        'Two interchangeable attachments',
        'Under 500g · USB-C rechargeable',
        'Two batteries + carry case',
        '2-year warranty'
      ]
    },

    herobelt: {
      handle: 'herobelt-red-light-therapy-belt',
      name: 'HeroBelt™',
      tag: 'Wearable wrap',
      specs: [
        '660 + 850nm (1:2) · 120 LEDs',
        '30 mW/cm² at contact',
        '53 x 18cm · 0.5kg',
        '20-minute timer · power bank capable',
        'Washable cover · 1-year warranty'
      ]
    },

    heroneck: {
      handle: 'heroneck™-red-light-therapy-device-for-upper-back-neck-and-traps',
      name: 'HeroNeck™',
      tag: 'Wearable wrap',
      specs: [
        '660 + 850nm (1:2) · 100 LEDs',
        '30 mW/cm² at contact',
        'Adds 45-55°C heat',
        'Wireless · USB-C · 10-30 min timer',
        '1-year warranty'
      ]
    },

    herohatled: {
      handle: 'herohat-red-light-therapy-hat',
      name: 'HeroHat LED™',
      tag: 'Wearable cap',
      specs: [
        '660 + 850nm (1:2) · 110 LEDs',
        '30 mW/cm² at contact',
        '30-minute timer',
        'Power-bank capable · hands-free',
        '1-year warranty'
      ]
    },

    herohatlaser: {
      handle: 'herohat-laser-red-light-therapy-hat',
      name: 'HeroHat Laser™',
      tag: 'Wearable cap',
      specs: [
        '128 x 5mW 650nm laser diodes',
        'Laser272 version: 272 diodes',
        '10Hz pulse · auto on-sensor',
        'Carry case included',
        'Bundled with HeroHat LED as the HeroHair package'
      ]
    },

    heromask: {
      handle: 'heromask-elite-red-light-therapy-mask',
      name: 'HeroMask Elite™',
      tag: 'Face + neck mask',
      specs: [
        '415 / 590 / 633 / 830 / 1072nm',
        '186 four-in-one LEDs (744 chips)',
        'Separate face and neck pieces',
        'Medical-grade silicone · 10-min sessions',
        '1-year warranty'
      ]
    },

    herobeauty: {
      handle: 'herobeauty',
      name: 'HeroBeauty™ 6-in-1',
      tag: 'Facial device',
      specs: [
        '465 / 600 / 620nm',
        'Six modes x three intensities',
        'EMS, RF, warm 38-42°C, cool 10-18°C',
        'USB-C rechargeable',
        '1-year warranty'
      ]
    },

    herofilter: {
      handle: 'herofilter-100-blue-light-blocking-glasses-fitover',
      name: 'HeroFilter™',
      tag: 'Blue light blockers',
      specs: [
        'Blocks 100% of blue light',
        'Blocks 99.99% of green light',
        'Fit-over design · small and large',
        '1-year warranty'
      ]
    }
  },

  /* ==========================================================================
     3. QUIZZES
     Each quiz has: menu card copy, an intro, questions, rules and results.
     To disable a quiz, either remove its block from the section in the theme
     editor, or set  enabled: false  on it here.
     ========================================================================== */
  quizzes: {

    /* ------------------------------------------------------------------
       QUIZ 1 - Which device suits me?
       ------------------------------------------------------------------ */
    'device-finder': {
      enabled: true,
      menuTitle: 'Which device suits me?',
      menuBlurb: 'Start from scratch. Four questions across goal, routine, budget and space.',
      menuMeta: '4 questions',
      heading: 'Four questions. One honest recommendation.',
      sub: 'No email required, no results held hostage. If two devices are close we will say so.',

      questions: [
        {
          id: 'goal',
          columns: 2,
          label: 'What are you mainly hoping to work on?',
          help: 'Pick the one that brought you here. You can use any device for more than one thing.',
          options: [
            { value: 'wellbeing', label: 'General wellbeing', desc: 'Energy, sleep, recovery, mitochondrial support - no single problem area' },
            { value: 'pain', label: 'A painful or stiff area', desc: 'Lower back, knee, shoulder, neck, hip' },
            { value: 'skin', label: 'Skin and face', desc: 'Collagen, fine lines, tone, blemishes' },
            { value: 'hair', label: 'Hair and scalp', desc: 'Thinning, density, scalp coverage' },
            { value: 'sport', label: 'Training and recovery', desc: 'Muscle soreness, sessions before or after training' },
            { value: 'sleep', label: 'Sleep and wind-down', desc: 'Evening light, getting to sleep, staying asleep' }
          ]
        },
        {
          id: 'session',
          columns: 2,
          label: 'How do you actually want to spend the session?',
          help: 'This is the question that decides whether the device still gets used in month three.',
          options: [
            { value: 'stand', label: 'Standing in front of it', desc: 'Ten to twenty minutes, front then back' },
            { value: 'lie', label: 'Lying down', desc: 'Flat on the floor, front and back at once' },
            { value: 'wear', label: 'Wearing it while I get on with things', desc: 'Hands free, moving around the house' },
            { value: 'desk', label: 'Sitting at a desk or bench', desc: 'Working, reading, having coffee' }
          ]
        },
        {
          id: 'budget',
          columns: 2,
          label: 'What is your budget?',
          help: 'We will tell you if spending more changes anything for your goal. Often it does not.',
          options: [
            { value: 'b1', label: 'Under $500', desc: 'Entry point, targeted devices' },
            { value: 'b2', label: '$500 to $1,500', desc: 'Tabletop panels, masks, laser caps' },
            { value: 'b3', label: '$1,500 to $3,000', desc: 'Half-body panels and full-body pods' },
            { value: 'b4', label: '$3,000 or more', desc: 'Full-body panels and multi-panel setups' }
          ]
        },
        {
          id: 'space',
          columns: 2,
          label: 'How much space can you give it?',
          help: 'A panel you have to move every session is a panel you stop using.',
          options: [
            { value: 'none', label: 'None to spare', desc: 'It needs to live in a drawer or cupboard' },
            { value: 'corner', label: 'A corner or a benchtop', desc: 'Something small that can stay out' },
            { value: 'wall', label: 'A clear wall or floor strip', desc: 'About 1m of clear space' },
            { value: 'room', label: 'A dedicated spot', desc: 'A home gym, spare room or garage corner' }
          ]
        }
      ],

      // Checked top to bottom. First match wins.
      rules: [
        { when: { goal: 'skin', budget: 'b1' }, result: 'skin_budget' },
        { when: { goal: 'skin' }, result: 'skin_mask' },

        { when: { goal: 'hair', budget: 'b1' }, result: 'hair_led' },
        { when: { goal: 'hair' }, result: 'hair_laser' },

        { when: { goal: 'pain', session: 'wear' }, result: 'pain_wrap' },
        { when: { goal: 'pain', space: 'none' }, result: 'pain_wrap' },
        { when: { goal: 'pain', budget: 'b1' }, result: 'pain_wrap' },
        { when: { goal: 'pain', budget: ['b3', 'b4'], space: ['wall', 'room'] }, result: 'pain_panel' },
        { when: { goal: 'pain' }, result: 'pain_spotlight' },

        { when: { goal: 'sleep' }, result: 'sleep_filter' },

        // Everything below is general wellbeing or sport.
        { when: { session: 'lie', budget: 'b4' }, result: 'whole_pod_elite' },
        { when: { session: 'lie' }, result: 'whole_pod' },
        { when: { space: 'none', budget: 'b4' }, result: 'whole_pod_elite' },
        { when: { space: 'none', budget: 'b3' }, result: 'whole_pod' },
        { when: { budget: 'b1' }, result: 'whole_spotlight' },
        { when: { budget: 'b2' }, result: 'whole_tabletop' },
        { when: { budget: 'b3' }, result: 'whole_half' }
      ],
      fallback: 'whole_full',

      // Applied after the result is chosen. All matching modifiers run.
      modifiers: [
        {
          when: { result: 'skin_mask', session: 'stand', budget: ['b3', 'b4'] },
          addAlternate: { product: 'hero300elite', why: 'If you also want body coverage, a half-body panel does skin and everything else - just less precisely around the nose and eyes.', position: 'start' }
        },
        {
          when: { goal: 'sport' },
          addWhy: { t: "Red and near-infrared light has been studied before and after training for fatigue and soreness. The picture is mixed: a meta-analysis of trials before exercise found improvements, while a 2024 meta-analysis found recovery benefits but no gain in strength in healthy people.", h: true, refs: ['muscle_meta_pre', 'muscle_meta_null', 'muscle_review'] }
        }
      ],

      results: {
        skin_budget: {
          product: 'herobeauty',
          eyebrow: 'Best match for you',
          why: [
            'A contact device is the practical pick for skin - it sits on the skin, needs no setup and takes no space.',
            'Six modes plus EMS, radiofrequency, warm and cool - it slots into a routine you already have.',
            'If you would rather have light only, with no EMS or radiofrequency, the HeroTorch covers spot work.'
          ],
          alternates: [
            { product: 'herotorch', why: 'Light only, five wavelengths, and useful for far more than the face.' },
            { product: 'heromask', why: 'The step up when budget allows: full face plus a separate neck piece.' }
          ]
        },
        skin_mask: {
          product: 'heromask',
          eyebrow: 'Best match for you',
          why: [
            '186 four-in-one LEDs across five wavelengths, held flush against the face with nothing to set up or aim.',
            'A separate neck and decolletage piece, which most masks leave out entirely.',
            { t: "Ten-minute sessions, three to six times a week - in the range the home-use mask trials used.", h: true, refs: ['skin_mask_630', 'skin_mask_home'] }
          ],
          note: { t: "The home-use trials behind this format used contact-fit masks like this one. A panel can be used on the face as well - choose on fit and convenience rather than on one being stronger than the other.", h: true, refs: ['skin_mask_630', 'skin_collagen_density'] },
          alternates: [
            { product: 'herobeauty', why: 'A cheaper add-on for between mask sessions, with EMS and radiofrequency.' }
          ]
        },

        hair_led: {
          product: 'herohatled',
          eyebrow: 'Best match for you',
          why: [
            '110 LEDs at 660 and 850nm across the whole scalp, hands free, on a 30-minute timer.',
            'Runs off a power bank, so the session happens while you move around rather than sitting still.',
            'The entry point for scalp coverage, hands free with nothing to aim.'
          ],
          alternates: [
            { product: 'herohatlaser', why: 'The laser version matches the diode specifications used in the key hair research.' }
          ]
        },
        hair_laser: {
          product: 'herohatlaser',
          eyebrow: 'Best match for you',
          why: [
            { t: "128 x 5mW 650nm laser diodes. Lasers at 650nm feature in the leading hair trials, including one using a 272-laser cap.", h: true, refs: ['hair_meta', 'hair_kim_2013'] },
            'A 272-diode version is available if you want the denser array.',
            { t: "Bundled with the HeroHat LED as the HeroHair package. Several of the trials combined 650nm lasers with red LEDs rather than using either on its own.", h: true, refs: ['hair_kim_2013', 'hair_lanzafame'] }
          ],
          note: { t: "Hair is slow. The trials on our research page ran 16 to 24 weeks, and one ran a full six months, so buy on the assumption you will still be using it next winter.", h: true, refs: ['hair_lanzafame', 'hair_kim_2013', 'hair_kim_2023'] },
          alternates: [
            { product: 'herohatled', why: 'The LED cap on its own, or as the other half of the bundle.' }
          ]
        },

        pain_wrap: {
          product: 'herobelt',
          eyebrow: 'Best match for you',
          why: [
            { t: "Wraps the sore area at contact distance. Its 30 mW/cm² is not a weak number - published human LED trials commonly sit in this range, and more is not automatically better.", h: true, refs: ['dose_low_irradiance', 'dose_biphasic', 'pain_review'] },
            'Twenty-minute timer, power-bank capable, washable cover - the session happens while you do something else.',
            'One area at a time. If your pain moves around, a panel is the better buy.'
          ],
          note: 'If more than one area bothers you, spending the same money on a panel usually goes further than buying two wraps.',
          alternates: [
            { product: 'heroneck', why: 'The same idea shaped for neck, traps and upper back, with added heat.' },
            { product: 'herotorch', why: 'Handheld and precise, for small spots and travel.' }
          ]
        },
        pain_panel: {
          product: 'hero300elite',
          eyebrow: 'Best match for you',
          why: [
            '91 x 29.8cm of emitting surface covers an entire back or both legs in one position.',
            { t: "Ten wavelengths from 590 to 1072nm, spanning the red and near-infrared bands used across the pain and inflammation research.", h: true, refs: ['pain_review', 'pain_tendinopathy'] },
            'Pain rarely stays in one place. A panel covers whatever is sore this month without buying another device.'
          ],
          alternates: [
            { product: 'herobelt', why: 'Add-on for the days you would rather not stand still.' },
            { product: 'herospotlight', why: 'A cheaper, portable way to aim light at one area hands free.' }
          ]
        },
        pain_spotlight: {
          product: 'herospotlight',
          eyebrow: 'Best match for you',
          why: [
            '143 mW/cm² at 7.5cm from an 18-LED head - measured at a usable working distance, not at the surface.',
            'The stand adjusts from 97 to 200cm, so you can aim it at a knee, a shoulder or a back while sitting or standing.',
            'Two kilos including the stand, folds to 40cm, lives in a cupboard.'
          ],
          alternates: [
            { product: 'herobelt', why: 'Wrap it on instead of aiming a lamp, if hands-free matters more than reach.' },
            { product: 'hero60elite', why: 'A tabletop panel with the full ten-wavelength spread when budget allows.' }
          ]
        },

        sleep_filter: {
          product: 'herofilter',
          eyebrow: 'Start here, it costs the least',
          why: [
            'Blocks 100% of blue light and 99.99% of green - most cheap blockers only cut part of the blue band.',
            { t: "Blue light in the evening is the thing most worth removing first. Red light has also been studied for sleep quality, so the two work together rather than being an either/or.", h: true, refs: ['sleep_athletes', 'sleep_nurses'] },
            'Fit-over design in two sizes, so it works over prescription glasses.'
          ],
          note: { t: "We would rather start you on the simplest thing that might help. If you want to add light as well as remove it, red light in the evening has been studied for sleep quality too.", h: true, refs: ['sleep_athletes', 'sleep_nurses'] },
          alternates: [
            { product: 'hero60elite', why: 'A tabletop panel for a morning or early-evening session, if you want light therapy as well as light hygiene.' },
            { product: 'herobelt', why: 'Hands-free evening sessions on the couch.' }
          ]
        },

        whole_pod: {
          product: 'herosnug',
          eyebrow: 'Best match for you',
          why: [
            '2,520 LEDs across 180 x 80cm, treating front and back at the same time - no turning around halfway.',
            'Unzips into two flat mats, then rolls up and goes in a cupboard between sessions.',
            'The only format here that does not need permanent floor or wall space.'
          ],
          alternates: [
            { product: 'herosnugelite', why: 'Six wavelengths including deep 1064nm, for a little more.' },
            { product: 'hero300elite', why: 'A half-body panel if you would rather stand than lie down.' }
          ]
        },
        whole_pod_elite: {
          product: 'herosnugelite',
          eyebrow: 'Best match for you',
          why: [
            '2,520 LEDs across 180 x 80cm, treating front and back at the same time - no turning around halfway.',
            'Six wavelengths including 1064nm, the longest wavelength in the range.',
            'Unzips into two flat mats, then rolls up and goes in a cupboard between sessions.'
          ],
          alternates: [
            { product: 'herosnug', why: 'The standard version if six wavelengths are not worth the difference to you.' },
            { product: 'hero300elite', why: 'A half-body panel if you would rather stand than lie down.' }
          ]
        },
        whole_spotlight: {
          product: 'herospotlight',
          eyebrow: 'Best match for you',
          why: [
            '143 mW/cm² at 7.5cm - measured at a usable working distance, not at the surface.',
            'Height-adjustable stand from 97 to 200cm, so you can treat any area hands free.',
            'It treats one area at a time, properly, rather than spreading thin light across all of you.'
          ],
          note: 'Be honest about coverage. This treats one region at a time - whole-body sessions start at the Hero300 size.',
          alternates: [
            { product: 'herotorch', why: 'Even more portable, five wavelengths, and it travels.' },
            { product: 'hero60elite', why: 'The next real step up - a tabletop panel with all ten wavelengths.' }
          ]
        },
        whole_tabletop: {
          product: 'hero60elite',
          eyebrow: 'Best match for you',
          why: [
            'Ten wavelengths from 590 to 1072nm in a panel that sits on a bench - the same spectrum as the full-body models.',
            { t: "172 mW/cm² at 7.5cm with 60° lenses, measured at a usable distance. Higher irradiance mainly buys a shorter session or more room to stand back - the human studies sit well below it.", h: true, refs: ['dose_low_irradiance', 'dose_biphasic'] },
            'Ten wavelengths against the five in the original Hero60, in the same size and the same footprint.'
          ],
          note: 'A tabletop panel treats one region at a time. Whole-body sessions start at the Hero300 size.',
          alternates: [
            { product: 'hero60', why: 'The original tabletop, five wavelengths, higher raw irradiance at 7.5cm.' },
            { product: 'herospotlight', why: 'If you would rather spend less and put the rest toward a bigger panel later.' }
          ]
        },
        whole_half: {
          product: 'hero300elite',
          eyebrow: 'Best match for you',
          why: [
            '91 x 29.8cm covers your whole torso or both legs at once - two positions and you have done the lot.',
            'Ten wavelengths, 0 μT EMF at 7.5cm, flicker free, three-year warranty.',
            { t: "The size we recommend most often. A bigger panel covers more of you per position, so it shortens the session rather than changing what lands on any one patch of skin.", h: true, refs: ['dose_biphasic'] }
          ],
          alternates: [
            { product: 'hero300', why: 'The original - five wavelengths, and HeroSync if you plan to build a multi-panel wall.' },
            { product: 'hero960elite', why: 'Full-body in one standing session, if the extra spend buys you time you value.' }
          ]
        },
        whole_full: {
          product: 'hero960elite',
          eyebrow: 'Best match for you',
          why: [
            '180 x 47.3cm treats you head to toe in one position - the shortest possible session.',
            '960 medical-grade LEDs across all ten wavelengths, 172 mW/cm² at 7.5cm.',
            'This is the right buy when time, not budget, is the binding constraint.'
          ],
          note: 'Budget about 2m x 1m of clear space and add a stand: the HS4 Branch Stand, or the HS5 Anti-Tip Wall Stand.',
          alternates: [
            { product: 'hero300elite', why: 'Far cheaper. Same spectrum, two positions instead of one.' },
            { product: 'hero960', why: 'The original full-body panel, with higher raw irradiance and red-only or NIR-only modes.' }
          ]
        }
      }
    },

    /* ------------------------------------------------------------------
       QUIZ 2 - Which panel size?
       ------------------------------------------------------------------ */
    'panel-size': {
      enabled: true,
      menuTitle: 'Which panel size should I get?',
      menuBlurb: 'Hero60, Hero300 or Hero960. Coverage, session length, floor space and price.',
      menuMeta: '4 questions',
      heading: 'Four questions and we will name a size',
      sub: 'Including the cases where the smaller panel is the right answer and the bigger one is a waste.',

      questions: [
        {
          id: 'coverage',
          columns: 2,
          label: 'How much of you needs light in a session?',
          help: 'Be realistic about the next twelve months, not just this week.',
          options: [
            { value: 'spot', label: 'One area', desc: 'A face, a knee, a shoulder, a patch of scalp' },
            { value: 'region', label: 'A region', desc: 'A whole back, a torso, both legs' },
            { value: 'whole', label: 'Everything', desc: 'Head to toe, front and back' }
          ]
        },
        {
          id: 'time',
          columns: 2,
          label: 'How much time will you give it, realistically?',
          help: 'A whole-body session on a smaller panel means repositioning. That is fine if you have the time and fatal if you do not.',
          options: [
            { value: 'short', label: 'Ten minutes, maximum', desc: 'It has to fit into an existing gap in my day' },
            { value: 'medium', label: 'Fifteen to twenty-five minutes', desc: 'I can reposition once or twice' },
            { value: 'long', label: 'As long as it takes', desc: 'I will read or stretch while it runs' }
          ]
        },
        {
          id: 'space',
          columns: 2,
          label: 'What space can the panel have?',
          help: 'Panels get used when they live where you already are. They stop getting used when they have to be carried.',
          options: [
            { value: 'bench', label: 'A benchtop or desk', desc: 'Nothing on the floor' },
            { value: 'wall', label: 'About a metre of wall', desc: 'It can lean or sit in a floor stand' },
            { value: 'room', label: 'A dedicated 2m x 1m area', desc: 'Home gym, spare room, garage corner' },
            { value: 'none', label: 'Nothing permanent', desc: 'It has to pack away every time' }
          ]
        },
        {
          id: 'budget',
          columns: 2,
          label: 'What is the ceiling?',
          options: [
            { value: 'b1', label: 'Around $1,000' },
            { value: 'b2', label: '$2,000 to $2,600' },
            { value: 'b3', label: '$3,000 to $4,000' },
            { value: 'b4', label: '$5,000 or more' }
          ]
        }
      ],

      rules: [
        { when: { coverage: 'spot' }, result: 'size_tabletop' },
        { when: { coverage: 'region', budget: 'b1' }, result: 'size_tabletop' },
        { when: { coverage: 'whole', time: 'short', budget: 'b4', space: 'room' }, result: 'size_full' },
        { when: { coverage: 'whole', time: 'short', budget: ['b3', 'b4'] }, result: 'size_half_fast' },
        { when: { coverage: 'whole', space: ['none', 'bench'] }, result: 'size_pod' },
        { when: { budget: 'b1' }, result: 'size_tabletop_budget' }
      ],
      fallback: 'size_half',

      modifiers: [
        {
          when: { result: 'size_half', time: 'short' },
          note: 'Heads up: with a ten-minute ceiling you will only get one or two positions done per session. Either alternate areas across the week, or move up to the Hero960 Elite.'
        }
      ],

      results: {
        size_tabletop: {
          product: 'hero60elite',
          eyebrow: 'The size we would buy in your position',
          why: [
            '31.6 x 21.4cm of emitting surface is the right size for one area at a time - a face, a knee, a shoulder.',
            'Ten wavelengths from 590 to 1072nm. You lose coverage going small, not spectrum.',
            '172 mW/cm² at 7.5cm with a built-in stand, so it works on a desk without buying anything else.'
          ],
          note: 'If you think you will want whole-body sessions within a year, buy the Hero300 Elite now. Upgrading later costs more than starting there.',
          alternates: [
            { product: 'hero60', why: 'The original tabletop: five wavelengths, and a slightly higher irradiance reading at 7.5cm.' },
            { product: 'herospotlight', why: 'A lamp on a stand if you want to aim light at one area rather than sit in front of a panel.' }
          ]
        },
        size_tabletop_budget: {
          product: 'hero60elite',
          eyebrow: 'What the budget buys',
          why: [
            'The smallest panel in the range, carrying the same ten wavelengths as the full-body models.',
            'It treats one region at a time. A whole-body pass is possible but slow.',
            'Built-in stand, 3kg, lives on a bench.'
          ],
          alternates: [
            { product: 'herosnug', why: 'If whole-body coverage matters more than panel format, the pod treats front and back at once.' },
            { product: 'hero300elite', why: 'The size most people should stretch for.' }
          ]
        },
        size_half: {
          product: 'hero300elite',
          eyebrow: 'The size we would buy in your position',
          why: [
            'This is the size we recommend most often.',
            '91 x 29.8cm covers half your body per position - front torso, back, front legs, back legs.',
            'It leans against a wall or drops into an HS1 floor stand. It does not need a dedicated room.'
          ],
          alternates: [
            { product: 'hero960elite', why: 'Full body in one position, if you would rather buy time than save money.' },
            { product: 'hero300', why: 'The original, with HeroSync if you plan to add panels later.' }
          ]
        },
        size_half_fast: {
          product: 'hero300elite',
          eyebrow: 'The size we would buy in your position',
          why: [
            '91 x 29.8cm covers a whole torso or both legs in one position - four positions for a full body pass.',
            'Ten wavelengths, 0 μT EMF at 7.5cm, flicker free, three-year warranty.',
            'Two of these share one upgraded HS4 Branch Stand, giving 182cm of stacked height you can angle inward around your body.'
          ],
          alternates: [
            { product: 'hero960elite', why: 'One position instead of four, if the extra spend buys back time you actually value.' },
            { product: 'herosnug', why: 'Front and back at once lying down, if standing is the part you will skip.' }
          ]
        },
        size_full: {
          product: 'hero960elite',
          eyebrow: 'The size we would buy in your position',
          why: [
            '180 x 47.3cm covers you head to toe. One position front, one position back, and you are done.',
            '960 LEDs across all ten wavelengths at 172 mW/cm² measured at 7.5cm - not at 0cm like most spec sheets you will see.',
            'This is the size that survives a ten-minute morning window. Nothing smaller does whole body that fast.'
          ],
          note: 'Add a stand: the HS4 Branch Stand for this size, or the HS5 Anti-Tip Wall Stand if you have a solid wall.',
          alternates: [
            { product: 'hero960', why: 'The original full-body panel with red-only and NIR-only modes.' },
            { product: 'hero300elite', why: 'Same spectrum in a third of the surface area - two extra positions per session.' }
          ]
        },
        size_pod: {
          product: 'herosnug',
          eyebrow: 'A panel is the wrong shape for your space',
          why: [
            'Whole-body coverage needs 1.8m of something. If it cannot be a panel on a wall, it has to be a mat on the floor.',
            '2,520 LEDs across 180 x 80cm treating front and back simultaneously, then it rolls up and goes away.',
            'Contact-distance LEDs on a flexible mat rather than a rigid framed panel, so it stores flat.'
          ],
          alternates: [
            { product: 'herosnugelite', why: 'Six wavelengths including deep 1064nm.' },
            { product: 'hero60elite', why: 'If whole-body is negotiable, a tabletop panel and a bit of patience covers the same ground region by region.' }
          ]
        }
      }
    },

    /* ------------------------------------------------------------------
       QUIZ 3 - Panel vs mask, belt or wearable
       ------------------------------------------------------------------ */
    'panel-vs-wearable': {
      enabled: true,
      menuTitle: 'Panel, or mask, belt and wearable?',
      menuBlurb: 'Coverage and power at a distance, against contact-distance light that costs you no time.',
      menuMeta: '4 questions',
      heading: 'Panel or wearable? Four questions.',
      sub: 'We will also tell you when the answer is one of each.',

      questions: [
        {
          id: 'areas',
          columns: 2,
          label: 'How many parts of your body are you treating?',
          help: 'This decides the format more than anything else does.',
          options: [
            { value: 'one', label: 'One, and it does not move', desc: 'Lower back, one knee, my face, my scalp' },
            { value: 'two', label: 'Two or three', desc: 'A couple of trouble spots' },
            { value: 'many', label: 'Whatever needs it that week', desc: 'It moves around, or I want general coverage' }
          ]
        },
        {
          id: 'where',
          columns: 2,
          label: 'Where is it?',
          options: [
            { value: 'face', label: 'Face and skin' },
            { value: 'scalp', label: 'Scalp and hair' },
            { value: 'neck', label: 'Neck, traps or upper back' },
            { value: 'body', label: 'Back, hips, knees, shoulders or legs' }
          ]
        },
        {
          id: 'still',
          columns: 2,
          label: 'Will you stand or sit still for ten to twenty minutes?',
          help: 'Answer for the version of you in month three, not the version reading an ad.',
          options: [
            { value: 'yes', label: 'Yes, happily', desc: 'I will use it as a break, or stretch while it runs' },
            { value: 'maybe', label: 'Some days', desc: 'If it is convenient, otherwise it will not happen' },
            { value: 'no', label: 'No chance', desc: 'It has to happen while I do something else' }
          ]
        },
        {
          id: 'budget',
          columns: 2,
          label: 'What are you willing to spend?',
          options: [
            { value: 'b1', label: 'Under $500' },
            { value: 'b2', label: '$500 to $1,200' },
            { value: 'b3', label: '$1,200 to $2,600' },
            { value: 'b4', label: 'Over $2,600' }
          ]
        }
      ],

      rules: [
        { when: { where: 'face', budget: 'b1' }, result: 'pvw_face_budget' },
        { when: { where: 'face' }, result: 'pvw_face_mask' },
        { when: { where: 'scalp', budget: 'b1' }, result: 'pvw_scalp_led' },
        { when: { where: 'scalp' }, result: 'pvw_scalp_laser' },
        { when: { where: 'neck' }, result: 'pvw_neck' },
        { when: { areas: 'one', still: ['maybe', 'no'] }, result: 'pvw_wrap' },
        { when: { areas: 'many', budget: 'b1' }, result: 'pvw_spotlight' },
        { when: { still: 'yes', budget: 'b1' }, result: 'pvw_spotlight' },
        { when: { areas: 'many', budget: 'b2' }, result: 'pvw_tabletop' },
        { when: { still: 'yes', budget: 'b2' }, result: 'pvw_tabletop' },
        { when: { areas: 'many' }, result: 'pvw_panel' },
        { when: { still: 'yes' }, result: 'pvw_panel' }
      ],
      fallback: 'pvw_wrap_default',

      modifiers: [
        {
          when: { result: 'pvw_face_mask', budget: ['b3', 'b4'] },
          addAlternate: { product: 'hero300elite', why: 'Add a half-body panel if you want the rest of you covered too.', position: 'start' }
        },
        {
          when: { result: 'pvw_neck', budget: ['b3', 'b4'] },
          addAlternate: { product: 'hero300elite', why: 'A half-body panel if the neck is only part of the story.' }
        },
        {
          when: { result: 'pvw_wrap', budget: ['b3', 'b4'] },
          addAlternate: { product: 'hero300elite', why: 'A panel if that one area might become three.' }
        }
      ],

      results: {
        pvw_face_budget: {
          product: 'herobeauty',
          eyebrow: 'Wearable wins here',
          why: [
            { t: "Red and near-infrared light has been studied for collagen, fine lines and skin texture.", h: true, refs: ['skin_split_face_rct', 'skin_collagen_density'] },
            'A contact-fit handheld that adds EMS, radiofrequency, warm and cool modes alongside the light.',
            'A tabletop panel covers the rest of you as well, but it needs bench space and a moment of setup each time.'
          ],
          alternates: [
            { product: 'heromask', why: 'The full mask with a separate neck piece when budget allows.' },
            { product: 'hero60elite', why: 'A tabletop panel if you want body coverage as well as face.' }
          ]
        },
        pvw_face_mask: {
          product: 'heromask',
          eyebrow: 'Wearable wins here',
          why: [
            '186 four-in-one LEDs across five wavelengths sitting flush on the skin, including a separate neck and decolletage piece.',
            { t: "Ten-minute sessions, three to six times a week - the home-use mask trials ran 9 to 12 minutes, two to five times a week.", h: true, refs: ['skin_mask_630', 'skin_mask_home'] },
            'A mask follows the shape of your face and needs no setup. A panel can be used on the face too, if you would rather cover more of yourself at once.'
          ],
          alternates: [
            { product: 'herobeauty', why: 'A cheaper handheld for between sessions.' }
          ]
        },
        pvw_scalp_led: {
          product: 'herohatled',
          eyebrow: 'Wearable wins here',
          why: [
            { t: "A cap sits directly on the scalp and covers all of it, hands free. The published hair trials used caps and helmets of this kind.", h: true, refs: ['hair_kim_2023', 'hair_lanzafame'] },
            '110 LEDs at 660 and 850nm, 30-minute timer, runs off a power bank while you walk around.',
            'Nothing to aim and nothing to stand in front of - it goes on and you carry on.'
          ],
          note: { t: "The hair trials ran 16 to 24 weeks, and one ran a full six months. Buy on the assumption you will still be doing this next winter.", h: true, refs: ['hair_lanzafame', 'hair_kim_2013', 'hair_kim_2023'] },
          alternates: [
            { product: 'herohatlaser', why: 'The laser cap, which matches the research specifications more closely.' }
          ]
        },
        pvw_scalp_laser: {
          product: 'herohatlaser',
          eyebrow: 'Wearable wins here',
          why: [
            { t: "128 x 5mW 650nm laser diodes. Lasers at 650nm feature in the leading hair trials, including one using a 272-laser cap.", h: true, refs: ['hair_meta', 'hair_kim_2013'] },
            'A 272-diode version is available, and the laser plus LED HeroHair package bundles both.',
            'Hands free, on a timer, and sitting directly against the scalp.'
          ],
          note: { t: "The hair trials ran 16 to 24 weeks, and one ran a full six months. Buy on the assumption you will still be doing this next winter.", h: true, refs: ['hair_lanzafame', 'hair_kim_2013', 'hair_kim_2023'] },
          alternates: [
            { product: 'herohatled', why: 'The LED cap, and the other half of the bundle.' }
          ]
        },
        pvw_neck: {
          product: 'heroneck',
          eyebrow: 'Wearable wins here',
          why: [
            'A wrap follows the curve of your neck and traps, and stays put while you work.',
            '100 LEDs at 660 and 850nm plus 45-55°C heat, wireless, on a 10 to 30 minute timer.',
            'Desk-friendly: the session happens while you work.'
          ],
          alternates: [
            { product: 'herobelt', why: 'The same idea in a wider wrap, if you also want lower back and knees covered.' }
          ]
        },
        pvw_wrap: {
          product: 'herobelt',
          eyebrow: 'Wearable wins here',
          why: [
            'One area, and you will not stand still for it. That is the exact case a wrap is built for.',
            '120 LEDs at 660 and 850nm at contact distance, 20-minute timer, power-bank capable, washable cover.',
            'The best device is the one you use four times a week, not the one with the biggest spec sheet.'
          ],
          alternates: [
            { product: 'herospotlight', why: 'A lamp on an adjustable stand if you would rather not wear anything.' }
          ]
        },
        pvw_wrap_default: {
          product: 'herobelt',
          eyebrow: 'Wearable wins here',
          why: [
            'One or two areas, and convenience matters more than coverage.',
            'Contact-distance light on a 20-minute timer, hands free.',
            'It leaves room in the budget to add a panel later if your goals broaden.'
          ],
          alternates: [
            { product: 'hero60elite', why: 'A tabletop panel with the full spectrum.' },
            { product: 'herospotlight', why: 'An aimable lamp on a stand.' }
          ]
        },
        pvw_spotlight: {
          product: 'herospotlight',
          eyebrow: 'Panel-style wins here',
          why: [
            'Multiple areas plus a small budget means you need something aimable rather than something worn.',
            '143 mW/cm² at 7.5cm from an 18-LED head, on a stand that adjusts from 97 to 200cm.',
            'Two kilos, folds to 40cm, so it is not really a space commitment either.'
          ],
          alternates: [
            { product: 'herobelt', why: 'A wrap instead, if one area dominates.' },
            { product: 'hero60elite', why: 'A proper tabletop panel when you are ready.' }
          ]
        },
        pvw_tabletop: {
          product: 'hero60elite',
          eyebrow: 'Panel wins here',
          why: [
            'Multiple areas means you want something you aim, not something you strap on and unstrap.',
            'Ten wavelengths from 590 to 1072nm - a wearable gives you two.',
            '172 mW/cm² at 7.5cm, and it sits on a bench rather than taking floor space.'
          ],
          alternates: [
            { product: 'herobelt', why: 'Add a wrap later for the one area you treat daily.' },
            { product: 'hero300elite', why: 'The half-body size, when you want a session to cover a whole back at once.' }
          ]
        },
        pvw_panel: {
          product: 'hero300elite',
          eyebrow: 'Panel wins here',
          why: [
            '91 x 29.8cm covers a whole back or both legs in one position. No wearable comes close on area.',
            'Ten wavelengths against the two in a wrap, and a far larger emitting surface.',
            'Areas that move around are the classic panel case - you are not buying a device per body part.'
          ],
          note: 'A common setup is one panel plus one wearable. The panel does the broad work, the wearable catches the days the panel does not happen.',
          alternates: [
            { product: 'herobelt', why: 'Pair it with a wrap for the days you will not stand still.' },
            { product: 'herosnug', why: 'A lie-down pod that treats front and back at once.' }
          ]
        }
      }
    },

    /* ------------------------------------------------------------------
       QUIZ 4 - Red Light Hero vs other brands
       ------------------------------------------------------------------ */
    'vs-other-brands': {
      enabled: true,
      menuTitle: 'How do you compare with other brands?',
      menuBlurb: 'What separates a $700 marketplace panel from a $6,000 imported one, and where we sit.',
      menuMeta: '3 questions',
      heading: 'What are you comparing us against?',
      sub: 'We will give you the honest version, including the cases where we would not switch either.',

      questions: [
        {
          id: 'against',
          columns: 2,
          label: 'What is the other option on your shortlist?',
          options: [
            { value: 'premium', label: 'A premium imported brand', desc: 'Well known, $4,000 to $8,000 for full body, heavy marketing' },
            { value: 'budget', label: 'A budget marketplace panel', desc: 'Under $1,000, unfamiliar brand, spec sheet full of big numbers' },
            { value: 'mid', label: 'Another mid-range brand', desc: 'Similar price, similar claims, hard to separate' },
            { value: 'none', label: 'Nothing specific yet', desc: 'Still working out what good looks like' }
          ]
        },
        {
          id: 'matters',
          columns: 2,
          label: 'What matters most in the decision?',
          options: [
            { value: 'spec', label: 'Getting the specifications right', desc: 'Wavelengths, irradiance, EMF, flicker' },
            { value: 'value', label: 'Not overpaying', desc: 'Same hardware, less brand tax' },
            { value: 'after', label: 'What happens after the sale', desc: 'Warranty, returns, someone to call' },
            { value: 'risk', label: 'Not getting burned', desc: 'It is a lot of money for something I cannot test first' }
          ]
        },
        {
          id: 'size',
          columns: 2,
          label: 'What size are you shopping for?',
          options: [
            { value: 'small', label: 'Tabletop or targeted', desc: 'Around $300 to $1,100' },
            { value: 'half', label: 'Half-body panel', desc: 'Around $2,000 to $3,000' },
            { value: 'full', label: 'Full body', desc: '$2,000 to $6,000' },
            { value: 'unsure', label: 'Not decided yet' }
          ]
        }
      ],

      rules: [
        { when: { size: 'small' }, result: 'brand_small' },
        { when: { size: 'full' }, result: 'brand_full' }
      ],
      fallback: 'brand_half',

      // This quiz builds its reasons from the modifiers rather than the result,
      // so the same three panels can be argued differently.
      modifiers: [
        {
          when: { against: 'premium' },
          addWhy: [
            'Premium imported panels and ours largely use the same class of single-chip medical-grade LED. The gap is usually margin, marketing and an app - not the diodes.',
            'Ten wavelengths from 590 to 1072nm, all of them red or near-infrared. Some competitors reach a higher wavelength count by including blue, which is not what the red light research is about.',
            'No app. That means no subscription, no account, and no device that loses features when a company changes its roadmap.'
          ],
          note: 'Where we lose: some premium brands offer four or five-year warranties against our three. If warranty length is the deciding factor, that is a fair reason to buy elsewhere.'
        },
        {
          when: { against: 'budget' },
          addWhy: [
            'Budget panels tend to publish irradiance measured at 0cm, where any panel looks enormous. Ours are published at 7.5, 15, 30, 45 and 60cm because that is where you actually stand.',
            'We visit the factories that build our products, audit suppliers through TUV, SGS and BSCI, and test incoming stock ourselves with spectrometers, solar meters and EMF readers.',
            'An ASIC-registered Australian company with a 60-day return window and someone who answers WhatsApp. That is what a marketplace listing cannot give you if something fails in month eight.'
          ],
          note: 'Where we lose: on sticker price, every time. If your budget is tight, our HeroTorch or HeroSpotlight is a fairer comparison than a cheap full-size panel.'
        },
        {
          when: { against: 'mid' },
          addWhy: [
            'When two brands look identical, the separators are measurement honesty, EMF and flicker figures, warranty length and who is left standing to service it.',
            'Our full comparison table sets a Hero300 Elite against nine named competitors, row by row, and shows the rows we lose as well as the ones we win.',
            'Ten equally distributed wavelengths with single-lens LEDs, 0 μT EMF at 7.5cm, flicker free, three-year warranty on all panels.'
          ]
        },
        {
          when: { against: 'none' },
          addWhy: [
            'Start with coverage and consistency, not brand. The size that matches your space and routine matters more than whose logo is on it.',
            'Then check three numbers on any brand you consider: irradiance with its measurement distance, the actual wavelengths, and the warranty term.',
            'We publish all three, plus EMF and flicker, on every panel page.'
          ]
        },
        {
          when: { matters: 'after' },
          addWhy: 'Sixty-day returns with no restocking fee, a three-year panel warranty, lifetime support after warranty expires, and a 90-Day Best Offer Guarantee that credits you the difference if we discount after you buy.'
        },
        {
          when: { matters: 'risk' },
          noteIfEmpty: 'The 60-day window exists for exactly this. Buy it, use it properly for six weeks, and send it back in as-new condition if it is not doing anything for you. Return shipping is on you and there is no restocking fee.'
        },
        {
          when: { resultNot: 'brand_half' },
          addAlternate: { product: 'hero300elite', why: 'The size we recommend most often, and the one in our published comparison table.' }
        },
        {
          when: { resultNot: 'brand_small' },
          addAlternate: { product: 'hero60elite', why: 'The smallest panel carrying the full ten-wavelength spectrum.' }
        },
        {
          when: { size: 'full' },
          addAlternate: { product: 'herosnug', why: 'Full-body coverage if a 1.8m panel is more than you want to house.' }
        }
      ],

      results: {
        brand_small: {
          product: 'hero60elite',
          eyebrow: 'What we would put in front of you',
          why: []
        },
        brand_half: {
          product: 'hero300elite',
          eyebrow: 'What we would put in front of you',
          why: []
        },
        brand_full: {
          product: 'hero960elite',
          eyebrow: 'What we would put in front of you',
          why: []
        }
      }
    },

    /* ------------------------------------------------------------------
       QUIZ 5 - Tabletop vs half-body vs full-body
       ------------------------------------------------------------------ */
    'coverage-tiers': {
      enabled: true,
      menuTitle: 'Tabletop, half-body or full-body?',
      menuBlurb: 'How much of you needs light at once, and how many minutes a whole-body pass costs.',
      menuMeta: '3 questions',
      heading: 'Three questions to find your tier',
      sub: 'The tier decides the price. Get this right and everything else is detail.',

      questions: [
        {
          id: 'body',
          columns: 2,
          label: 'Honestly, how much of you needs light?',
          options: [
            { value: 'one', label: 'One area, most of the time', desc: 'A knee, a shoulder, my face, my lower back' },
            { value: 'half', label: 'Big regions', desc: 'A whole back, a whole set of legs, my torso' },
            { value: 'all', label: 'All of me', desc: 'Whole-body sessions are the point' }
          ]
        },
        {
          id: 'minutes',
          columns: 2,
          label: 'How many minutes per session, all in?',
          help: 'Including repositioning. This is where tabletop panels quietly fail people.',
          options: [
            { value: 'm10', label: 'Ten', desc: 'It has to fit a gap I already have' },
            { value: 'm20', label: 'Twenty', desc: 'One reposition is fine' },
            { value: 'm40', label: 'Forty or more', desc: 'I will read, stretch or work while it runs' }
          ]
        },
        {
          id: 'money',
          columns: 2,
          label: 'What is the budget?',
          options: [
            { value: 'b1', label: 'Around $1,000' },
            { value: 'b2', label: '$2,000 to $2,600' },
            { value: 'b3', label: '$2,600 to $4,000' },
            { value: 'b4', label: '$5,000 or more' }
          ]
        }
      ],

      rules: [
        { when: { body: 'one' }, result: 'tier_tabletop' },
        { when: { body: 'half', money: 'b1' }, result: 'tier_tabletop' },
        { when: { body: 'all', minutes: 'm10', money: 'b4' }, result: 'tier_full' },
        { when: { body: 'all', minutes: 'm40' }, result: 'tier_pod' },
        { when: { body: 'all', money: 'b2' }, result: 'tier_pod' },
        { when: { money: ['b3', 'b4'] }, result: 'tier_half_rich' }
      ],
      fallback: 'tier_half',

      modifiers: [
        {
          when: { result: 'tier_half_rich', minutes: 'm10' },
          note: 'With a ten-minute ceiling you will get one or two positions per session. Either rotate areas across the week or move up a tier.'
        }
      ],

      results: {
        tier_tabletop: {
          product: 'hero60elite',
          eyebrow: 'Tabletop tier',
          why: [
            'One area at a time is exactly what 31.6 x 21.4cm is for. Buying bigger would be buying surface you never switch on.',
            'Ten wavelengths from 590 to 1072nm and 172 mW/cm² at 7.5cm - you lose coverage going small, not spectrum or power.',
            'Built-in stand, 3kg, sits on a bench and never needs to be moved.'
          ],
          alternates: [
            { product: 'herospotlight', why: 'Cheaper again, on an adjustable stand, if aiming beats sitting.' },
            { product: 'hero300elite', why: 'The half-body tier, if there is any chance your goals broaden within a year.' }
          ]
        },
        tier_half: {
          product: 'hero300elite',
          eyebrow: 'Half-body tier',
          why: [
            'Big regions is exactly the half-body case: one position per side rather than shuffling a small panel around.',
            'The same ten wavelengths and the same 172 mW/cm² at 7.5cm as the full-body model, over a third of the area.',
            'Leans on a wall or drops into an HS1 floor stand. It does not need a dedicated room.'
          ],
          alternates: [
            { product: 'hero300', why: 'The original, with HeroSync for linking up to four panels later.' },
            { product: 'hero60elite', why: 'The tabletop tier, if one area really is the whole story.' }
          ]
        },
        tier_half_rich: {
          product: 'hero300elite',
          eyebrow: 'Half-body tier',
          why: [
            '91 x 29.8cm covers a whole torso or both legs per position - four positions and you have done everything.',
            'Ten wavelengths, 0 μT EMF at 7.5cm, flicker free, three-year warranty.',
            'Above this size you are mainly buying shorter sessions, not more coverage per position.'
          ],
          alternates: [
            { product: 'hero960elite', why: 'Full body in two positions instead of four.' },
            { product: 'herosnug', why: 'Front and back at once lying down.' }
          ]
        },
        tier_full: {
          product: 'hero960elite',
          eyebrow: 'Full-body tier',
          why: [
            '180 x 47.3cm covers you head to toe. Front, back, done, inside ten minutes.',
            '960 medical-grade LEDs across all ten wavelengths at 172 mW/cm² measured at 7.5cm.',
            'At a ten-minute ceiling with whole-body goals, this is the only tier that delivers. Everything smaller means skipping areas.'
          ],
          note: 'Budget 2m x 1m of clear space and a stand: HS4 Branch Stand, or HS5 Anti-Tip Wall Stand.',
          alternates: [
            { product: 'hero960', why: 'The original full-body with red-only and NIR-only modes.' },
            { product: 'hero300elite', why: 'A third of the emitting surface, if you can find twenty minutes instead of ten.' }
          ]
        },
        tier_pod: {
          product: 'herosnug',
          eyebrow: 'Full-body, lying down',
          why: [
            '2,520 LEDs across 180 x 80cm treating front and back simultaneously - one position for a whole-body pass.',
            'It is the smallest-footprint route to genuine full-body coverage - it rolls away when you are done.',
            'Unzips into two mats and rolls away, so it does not claim a wall permanently.'
          ],
          alternates: [
            { product: 'herosnugelite', why: 'Six wavelengths including deep 1064nm.' },
            { product: 'hero300elite', why: 'A standing half-body panel if you would rather not lie on the floor.' }
          ]
        }
      }
    },

    /* ------------------------------------------------------------------
       QUIZ 6 - Budget, room size or routine
       ------------------------------------------------------------------ */
    'budget-space-routine': {
      enabled: true,
      menuTitle: 'Choose by budget, space or routine',
      menuBlurb: 'Start from whichever constraint is tightest rather than from the goal.',
      menuMeta: '3 questions',
      heading: 'Name your constraint',
      sub: 'Three questions. The first one changes the two that follow.',

      questions: [
        {
          id: 'bind',
          columns: 2,
          label: 'What is the real limit here?',
          help: 'Pick the one you would not be able to talk yourself out of.',
          options: [
            { value: 'budget', label: 'Money', desc: 'There is a number and it is firm' },
            { value: 'space', label: 'Space', desc: 'The house decides, not the wishlist' },
            { value: 'routine', label: 'Time and routine', desc: 'If it does not fit my day it will not happen' }
          ]
        },
        {
          id: 'detail',
          columns: 2,
          label: 'Give us the specifics',
          options: [
            { value: 'a', label: 'Under $500, or nowhere to put anything, or no spare minutes at all', desc: 'The tightest version' },
            { value: 'b', label: 'Around $1,000, or a benchtop, or ten spare minutes', desc: 'Some room to move' },
            { value: 'c', label: 'Around $2,000-2,500, or a metre of wall, or twenty minutes', desc: 'Comfortable' },
            { value: 'd', label: 'Over $3,000, or a dedicated space, or as long as it takes', desc: 'No real limit' }
          ]
        },
        {
          id: 'goal',
          columns: 2,
          label: 'And what is it for?',
          options: [
            { value: 'body', label: 'Body, pain or recovery' },
            { value: 'whole', label: 'General wellbeing, whole body' },
            { value: 'skin', label: 'Skin and face' },
            { value: 'hair', label: 'Hair and scalp' }
          ]
        }
      ],

      rules: [
        // Skin and hair pick their own format regardless of the constraint.
        //
        // IMPORTANT: question 2 ("detail") means three different things
        // depending on question 1 ("bind") - a budget, a space, or an amount of
        // time. So only read it as a budget when they actually chose Money.
        // Otherwise we have no budget signal and should recommend the better
        // device for the goal, with the cheaper one offered alongside it.
        { when: { goal: 'skin', bind: 'budget', detail: 'a' }, result: 'bsr_skin_budget' },
        { when: { goal: 'skin' }, result: 'bsr_skin_mask' },
        { when: { goal: 'hair', bind: 'budget', detail: 'a' }, result: 'bsr_hair_led' },
        { when: { goal: 'hair' }, result: 'bsr_hair_laser' },

        { when: { bind: 'space', detail: 'a' }, result: 'bsr_space_none' },
        { when: { bind: 'space', detail: 'b' }, result: 'bsr_space_bench' },
        { when: { bind: 'space', detail: 'c' }, result: 'bsr_space_wall' },
        { when: { bind: 'space', detail: 'd' }, result: 'bsr_space_room' },

        { when: { bind: 'routine', detail: 'a' }, result: 'bsr_time_none' },
        { when: { bind: 'routine', detail: 'b' }, result: 'bsr_time_ten' },
        { when: { bind: 'routine', detail: 'c' }, result: 'bsr_time_twenty' },
        { when: { bind: 'routine', detail: 'd' }, result: 'bsr_time_open' },

        { when: { bind: 'budget', detail: 'a' }, result: 'bsr_budget_a' },
        { when: { bind: 'budget', detail: 'b' }, result: 'bsr_budget_b' },
        { when: { bind: 'budget', detail: 'c' }, result: 'bsr_budget_c' }
      ],
      fallback: 'bsr_budget_d',

      modifiers: [
        {
          when: { result: 'bsr_budget_a', goal: 'whole' },
          note: 'You want whole-body coverage but picked the tightest option. A targeted device cannot do that - either treat one region at a time for now, or step up to the HeroSnug or a Hero300-size panel.'
        },
        {
          when: { result: 'bsr_skin_mask', detail: 'd' },
          addAlternate: { product: 'hero300elite', why: 'Add a half-body panel if you want body coverage as well.' }
        }
      ],

      results: {
        bsr_skin_budget: {
          product: 'herobeauty',
          eyebrow: 'Skin picks its own format',
          why: [
            'Six modes with EMS, radiofrequency, warm and cool, in a handheld that works area by area.',
            'It needs no floor space and works while you move around, so space and routine stop being constraints.',
            'Ten-minute sessions, three to six times a week.'
          ],
          alternates: [
            { product: 'heromask', why: 'The full mask when the budget allows.' }
          ]
        },
        bsr_skin_mask: {
          product: 'heromask',
          eyebrow: 'Skin picks its own format',
          why: [
            { t: "186 four-in-one LEDs across five wavelengths at contact fit, plus a separate neck and decolletage piece. Both face and neck devices of this kind have been trialled.", h: true, refs: ['skin_mask_630', 'skin_neck_device'] },
            'It needs no floor space and works while you move around, so space and routine stop being constraints.',
            'Ten-minute sessions, three to six times a week.'
          ],
          alternates: [
            { product: 'herobeauty', why: 'A cheaper handheld for between sessions.' }
          ]
        },
        bsr_hair_led: {
          product: 'herohatled',
          eyebrow: 'Hair picks its own format',
          why: [
            { t: "110 LEDs across the whole scalp on a 30-minute timer. All the controlled hair-efficacy evidence sits in the 625-660nm red band; no trial has isolated near-infrared for hair.", h: true, refs: ['hair_kim_2023'] },
            'Zero space required and zero minutes added to your day - it runs off a power bank while you move around.',
            { t: "The published hair trials used caps and helmets worn on the scalp, which is what these are.", h: true, refs: ['hair_kim_2023', 'hair_meta'] }
          ],
          alternates: [
            { product: 'herohatlaser', why: 'The laser cap, and the other half of the HeroHair package.' }
          ]
        },
        bsr_hair_laser: {
          product: 'herohatlaser',
          eyebrow: 'Hair picks its own format',
          why: [
            { t: "128 x 5mW 650nm laser diodes. Lasers at 650nm feature in the leading hair trials.", h: true, refs: ['hair_meta', 'hair_kim_2013'] },
            'Zero space required and zero minutes added to your day - it runs off a power bank while you move around.',
            { t: "The published hair trials used caps and helmets worn on the scalp, which is what these are.", h: true, refs: ['hair_kim_2023', 'hair_meta'] }
          ],
          alternates: [
            { product: 'herohatled', why: 'The LED cap, and the other half of the HeroHair package.' }
          ]
        },

        bsr_space_none: {
          product: 'herobelt',
          eyebrow: 'Nothing to store, nothing to install',
          why: [
            'It lives in a drawer and the session happens while you sit down anyway.',
            { t: "120 LEDs at 660 and 850nm at contact distance, 20-minute timer, power-bank capable. Both wavelengths appear across the pain and inflammation research.", h: true, refs: ['pain_review', 'pain_hand_oa'] },
            'When there is genuinely no space, a wearable is the practical answer - the session happens with no setup at all.'
          ],
          alternates: [
            { product: 'herotorch', why: 'Even smaller, five wavelengths, and it travels.' },
            { product: 'herospotlight', why: 'Folds to 40cm and lives in a cupboard, if you want something aimable.' }
          ]
        },
        bsr_space_bench: {
          product: 'hero60elite',
          eyebrow: 'Benchtop tier',
          why: [
            '31.6 x 21.4cm with a built-in stand - it sits on a bench and never touches the floor.',
            'Ten wavelengths from 590 to 1072nm, the same spectrum as the full-body panels.',
            'Treats one area at a time, which is what a benchtop footprint can honestly do.'
          ],
          alternates: [
            { product: 'herospotlight', why: 'A lamp on an adjustable stand that folds away entirely.' },
            { product: 'herosnug', why: 'Full-body coverage that rolls up into a cupboard.' }
          ]
        },
        bsr_space_wall: {
          product: 'hero300elite',
          eyebrow: 'One metre of wall',
          why: [
            '91 x 29.8cm leans against a wall or drops into an HS1 floor stand. It does not need a room of its own.',
            'Four positions covers your whole body, ten wavelengths, three-year warranty.',
            'This is the largest panel that fits comfortably in a normal home without rearranging furniture.'
          ],
          alternates: [
            { product: 'herosnug', why: 'If even a metre of wall is optimistic - the pod rolls out and rolls away.' },
            { product: 'hero60elite', why: 'The benchtop option if the wall is not really available.' }
          ]
        },
        bsr_space_room: {
          product: 'hero960elite',
          eyebrow: 'A space that can take it',
          why: [
            '180 x 47.3cm, head to toe in one position, in a spot that can hold 2m x 1m permanently.',
            '960 LEDs across all ten wavelengths at 172 mW/cm² measured at 7.5cm.',
            'When space is not the constraint, this is the tier that buys back the most time.'
          ],
          note: 'Add a stand: the HS4 Branch Stand, or the HS5 Anti-Tip Wall Stand if you have a solid wall.',
          alternates: [
            { product: 'hero300elite', why: 'A third of the emitting surface, four positions instead of two.' },
            { product: 'herosnug', why: 'A pod alongside the panel if anyone in the house prefers lying down.' }
          ]
        },

        bsr_time_none: {
          product: 'herobelt',
          eyebrow: 'Zero minutes added to your day',
          why: [
            'No spare minutes means the session has to happen inside something you are already doing.',
            'Wrap it on at a desk or on the couch, 20-minute timer, hands completely free.',
            'A panel only helps if you stand in front of it. A wrap you actually wear four times a week is the one that gets used.'
          ],
          alternates: [
            { product: 'heroneck', why: 'The same idea shaped for neck, traps and upper back, with added heat.' },
            { product: 'herohatled', why: 'If the scalp is part of the picture, this runs while you walk around.' }
          ]
        },
        bsr_time_ten: {
          product: 'herospotlight',
          eyebrow: 'Ten minutes, one area',
          why: [
            '143 mW/cm² at 7.5cm from a lamp on a stand that adjusts 97 to 200cm - aim it and sit down.',
            { t: "Ten minutes on one area gets you into the range the studies used. Ten minutes spread across a whole body does not.", h: true, refs: ['dose_biphasic'] },
            'Two kilos, folds to 40cm, so it appears and disappears around your routine.'
          ],
          alternates: [
            { product: 'hero60elite', why: 'A proper tabletop panel with the full ten-wavelength spread.' },
            { product: 'herobelt', why: 'Hands free instead of aimed, if even sitting still is a stretch.' }
          ]
        },
        bsr_time_twenty: {
          product: 'hero300elite',
          eyebrow: 'Twenty minutes, most of you',
          why: [
            'Twenty minutes buys you two positions on a half-body panel - front and back of your torso, or both sides of your legs.',
            'Alternate upper and lower across the week and you cover everything without ever standing there longer.',
            'Ten wavelengths, 0 μT EMF at 7.5cm, three-year warranty.'
          ],
          alternates: [
            { product: 'hero960elite', why: 'If twenty minutes needs to cover absolutely everything, every time.' },
            { product: 'herosnug', why: 'Lie down and let it run for as long as you like.' }
          ]
        },
        bsr_time_open: {
          product: 'herosnug',
          eyebrow: 'Time is not the problem',
          why: [
            'If you will happily lie there for half an hour, the pod does the whole body in one position.',
            '2,520 LEDs across 180 x 80cm treating front and back at once, on a 10 to 90 minute timer.',
            'No standing, no repositioning, and it rolls away afterwards.'
          ],
          alternates: [
            { product: 'herosnugelite', why: 'Six wavelengths including deep 1064nm.' },
            { product: 'hero300elite', why: 'A standing panel with ten wavelengths, if you would rather not be on the floor.' }
          ]
        },

        bsr_budget_a: {
          product: 'herotorch',
          eyebrow: 'Under $500',
          why: [
            'Five wavelengths, two interchangeable attachments, two batteries and a carry case.',
            'It treats one area at a time, properly, rather than spreading thin light across all of you.',
            'Two-year warranty - longer than most of our wearables.'
          ],
          note: 'Be clear-eyed about coverage. This treats one region at a time - whole-body sessions start at the Hero300 size or the HeroSnug.',
          alternates: [
            { product: 'herospotlight', why: 'A lamp on a stand for hands-free sessions.' },
            { product: 'herobelt', why: 'A wrap if the area is a back, knee or shoulder.' }
          ]
        },
        bsr_budget_b: {
          product: 'hero60elite',
          eyebrow: 'Around $1,000',
          why: [
            'Ten wavelengths from 590 to 1072nm in a panel that sits on a bench - the same spectrum as the full-body model.',
            { t: "172 mW/cm² at 7.5cm with 60° lenses and a built-in stand. Higher irradiance mainly buys a shorter session, not a better outcome.", h: true, refs: ['dose_low_irradiance', 'dose_biphasic'] },
            'Ten wavelengths against the five in the original Hero60, in the same size and the same footprint.'
          ],
          alternates: [
            { product: 'hero60', why: 'The original tabletop: five wavelengths, and a slightly higher irradiance reading at 7.5cm.' },
            { product: 'herospotlight', why: 'Spend less here and put the rest toward a half-body panel later.' }
          ]
        },
        bsr_budget_c: {
          product: 'hero300elite',
          eyebrow: 'Around $2,000 to $2,500',
          why: [
            '91 x 29.8cm of emitting surface - four positions for a whole-body pass.',
            'Ten wavelengths, flicker free, 0 μT EMF at 7.5cm, three-year warranty.',
            'Above this tier you are mainly buying shorter sessions.'
          ],
          alternates: [
            { product: 'herosnug', why: 'Full-body coverage lying down - cheaper, and one position instead of four.' },
            { product: 'hero300', why: 'The original, with HeroSync for building a multi-panel wall later.' }
          ]
        },
        bsr_budget_d: {
          product: 'hero960elite',
          eyebrow: 'Over $3,000',
          why: [
            'Head to toe in one position: 180 x 47.3cm, 960 LEDs, all ten wavelengths.',
            'At this size the question stops being coverage and becomes how much time you want back.',
            'Three-year warranty, no app, no subscription, physical remote.'
          ],
          alternates: [
            { product: 'hero300elite', why: 'Two of these share one upgraded HS4 stand, which lets you angle the light inward rather than facing one flat panel.' },
            { product: 'herosnugelite', why: 'A pod alongside the panel, for the household members who would rather lie down.' }
          ]
        }
      }
    }
  }
};